import React from 'react';
import { Button, Typography } from '@/components/common';
import { HttpResponse, RequiredField, SignDocumentDetails } from '@/interfaces';
import { getFormattedTableDate } from '@/lib';
import clsx from 'clsx';

const IMAGE_SIGNATURE_REGEX = /^(https?:\/\/|data:image\/)/i;

interface DocumentFields {
	document_type: string;
	status?: string;
	created: string;
	signed?: string | null;
	file_size?: string;
	hide_signing_date?: boolean;
}

interface Props {
	actionDetails: HttpResponse<SignDocumentDetails> | undefined;
	isSignRequired: boolean | undefined;
	documentSignature: string;
	fields: RequiredField[];
	showValidateButton?: boolean;
	validateDisabled?: boolean;
	onValidate?: () => void;
	onInvalidate?: () => void;
	invalidateDisabled?: boolean;
	fillHeight?: boolean;
}

export const DocumentInformationSection: React.FC<Props> = ({
	actionDetails,
	isSignRequired,
	documentSignature,
	fields,
	showValidateButton,
	validateDisabled,
	onValidate,
	onInvalidate,
	invalidateDisabled,
	fillHeight = false,
}) => {
	const [documentFields, setDocumentFields] = React.useState<DocumentFields>();

	const getStatusLabel = (details: SignDocumentDetails): string => {
		if (!details.is_approved) {
			return 'Declined';
		}
		if (
			details.is_approved &&
			details.signature &&
			details.signature !== 'null'
		) {
			return 'Signed';
		}
		if (
			details.is_approved &&
			(!details.signature || details.signature === 'null')
		) {
			return 'Approved';
		}

		return '';
	};

	React.useEffect(() => {
		if (!actionDetails?.data) return;
		const details = actionDetails.data;

		const fields: DocumentFields = {
			document_type: details.name_doc,
			...(getStatusLabel(details) && { status: getStatusLabel(details) }),
			created: getFormattedTableDate(details.created_at),
			...(details.signed_at &&
				details.signature &&
				details.signature !== 'null' && {
					signed: getFormattedTableDate(details.signed_at),
				}),
			...(details.file_size && { file_size: `${details.file_size} MB` }),
			hide_signing_date: details.hide_signing_date,
		};

		setDocumentFields(fields);
	}, [actionDetails]);
	const showPlaintiff =
		(isSignRequired && documentSignature) ||
		(fields.length > 0 && actionDetails?.data.is_approved);

	return (
		<div
			className={clsx({
				'h-full min-h-0 flex flex-col gap-4': fillHeight,
				'min-h-full flex flex-col': !fillHeight && showValidateButton,
			})}
		>
			<div
				className={clsx('border border-grey-100 rounded-2xl p-4', {
					'shrink-0': fillHeight,
				})}
			>
				<Typography type="semibold-14" className="text-left mb-4">
					Document Information
				</Typography>
				<div className="space-y-3">
					{documentFields &&
						Object.entries(documentFields).map(([key, value], index) => {
							if (key === 'hide_signing_date') return null;
							return (
								<div
									key={index}
									className="flex flex-col items-baseline justify-between"
								>
									<Typography
										type="regular-12"
										className="text-gray-35 capitalize"
									>
										{key.replace('_', ' ')}:
									</Typography>
									<Typography
										type="regular-12"
										className={clsx('mt-1', {
											'!text-green-120':
												value === 'Signed' || value === 'Approved',
											'text-main-red': value === 'Declined',
										})}
									>
										{value}
									</Typography>
								</div>
							);
						})}
				</div>
			</div>
			{showPlaintiff && (
				<div
					className={clsx('border border-grey-100 rounded-2xl py-4', {
						'mt-6': !fillHeight,
						'flex-1 min-h-0 flex flex-col': fillHeight,
					})}
				>
					<Typography
						type="semibold-14"
						className={clsx('text-left mb-4 px-4', { 'shrink-0': fillHeight })}
					>
						Plaintiff Information
					</Typography>
					{fields.length > 0 && actionDetails?.data.is_approved && (
						<div
							className={clsx('overflow-y-auto scroll-custom px-4 space-y-3', {
								'lg:max-h-[32vh]': !fillHeight,
								'flex-1 min-h-0': fillHeight,
							})}
						>
							{fields
								.filter((f) => f.type !== 'SIGNATURE')
								.map((f, index) => {
									return (
										<div
											key={index}
											className="flex flex-col items-baseline justify-between"
										>
											<Typography
												type="regular-12"
												className="text-gray-35 capitalize text-left"
											>
												{f.question}:
											</Typography>
											<Typography
												type="regular-12"
												className={clsx('text-left mt-1 break-all')}
											>
												{f.response}
											</Typography>
										</div>
									);
								})}
						</div>
					)}
					{documentSignature &&
						documentSignature !== 'null' &&
						!IMAGE_SIGNATURE_REGEX.test(documentSignature) && (
							<div className="flex items-center gap-0.5 pl-4">
								<Typography type="regular-12" className="text-black">
									/s/
								</Typography>
								<Typography type="regular-28" className="!font-praise">
									{documentSignature}
								</Typography>
							</div>
						)}
				</div>
			)}

			{showValidateButton && (
				<div
					className={clsx('flex justify-end gap-2 mt-auto', {
						'pt-5': !fillHeight,
						'shrink-0': fillHeight,
					})}
				>
					<Button
						decoration="danger-outline"
						size="small"
						onClick={onInvalidate}
						disabled={invalidateDisabled}
					>
						Invalidate
					</Button>
					<Button
						decoration="primary"
						size="small"
						onClick={onValidate}
						disabled={validateDisabled}
					>
						Validate
					</Button>
				</div>
			)}
		</div>
	);
};
